import {
  aromaDatepicker,
  AromaDatepickerVariantProps,
} from "@zenchef/styled-system/recipes";
import { Translations } from "./types";
import Root, { DatePickerRootProps } from "./Root";
import Views from "./Views";
import Portal from "./Portal";
import mapStringToDateValue from "./functions/mapStringToDateValue";
import { ValueChangeDetails } from "@ark-ui/react/dist/components/date-picker/date-picker";
import { padDateValue } from "../../../utils/padDateValue";

export interface DatePickerProps
  extends Omit<
      DatePickerRootProps,
      "translations" | "defaultValue" | "value" | "focusedValue"
    >,
    AromaDatepickerVariantProps {
  defaultValue?: string[];
  value?: string[];
  focusedValue?: string;
  translations: Translations;
  withDoubleArrows?: boolean;
  hideDateOutsideRange?: boolean;
}

function DatePicker({
  translations,
  withDoubleArrows = true,
  defaultValue: defaultValueString,
  value: valueString,
  focusedValue: focusedValueString,
  closeOnSelect = true,
  startOfWeek = 1,
  ...propsWithVariants
}: DatePickerProps) {
  const [variants] = aromaDatepicker.splitVariantProps(propsWithVariants);
  const classes = aromaDatepicker(variants);

  const { value, defaultValue, focusedValue } = mapStringToDateValue({
    value: valueString,
    defaultValue: defaultValueString,
    focusedValue: focusedValueString,
  });

  const handleChange = (value: ValueChangeDetails) => {
    const copyValue = {
      ...value,
      valueAsString: value.value.map(
        (date) =>
          `${date.year}-${padDateValue(date.month)}-${padDateValue(date.day)}`,
      ),
    };

    return propsWithVariants?.onValueChange?.(copyValue);
  };

  return (
    <Root
      value={value}
      defaultValue={defaultValue}
      focusedValue={focusedValue}
      closeOnSelect={closeOnSelect}
      startOfWeek={startOfWeek}
      {...propsWithVariants}
      onValueChange={handleChange}
    >
      <Views
        classes={classes}
        defaultValue={defaultValue}
        focusedValue={focusedValue}
        withDoubleArrows={withDoubleArrows}
        translations={translations}
      />
    </Root>
  );
}

DatePicker.Root = Root;
DatePicker.Portal = Portal;
DatePicker.format = "YYYY-MM-DD";

export default DatePicker;
